import React from "react";
import Navbar from "../../components/Navbar";
import { Check, X, ArrowRight, Zap, DollarSign, Gauge, RefreshCw } from "lucide-react";

const comparisonData = {
  weaviate: {
    name: "Weaviate",
    tagline: "Modular vector database with hybrid search & GraphQL",
    pros: [
      "Built-in semantic search with hybrid capabilities",
      "Strong GraphQL and REST APIs",
      "Modular extensions (text2vec, transformers)",
      "Cloud and self-hosted flexibility",
      "Good balance of features and scale"
    ],
    cons: [
      "Slightly steeper learning curve",
      "Operational overhead for scale",
      "Performance varies by deployment config",
      "Monitoring requires 3rd-party tools",
      "Index tuning needed for some use cases"
    ],
    pricing: {
      tier: "Free / Moderate",
      description: "Open-source with predictable costs for self-hosted. Managed options vary by provider.",
      note: "Best for: Hybrid search use cases & flexible deployments"
    },
    performance: {
      latency: "Competitive at small–medium scale",
      throughput: "Strong for mixed workloads"
    }
  },

  milvus: {
    name: "Milvus",
    tagline: "Distributed vector database built for massive scale",
    pros: [
      "Designed for billion-scale vector search",
      "Highly scalable distributed architecture",
      "Strong performance for large AI workloads",
      "Cloud-native with Kubernetes support",
      "Backed by a mature open-source ecosystem"
    ],
    cons: [
      "Operationally complex to manage",
      "Higher infrastructure requirements",
      "Steeper learning curve",
      "Overkill for small projects",
      "Resource-intensive at scale"
    ],
    pricing: {
      tier: "Infrastructure-Based",
      description: "Open-source and free to use, but infrastructure and operational costs can be high at scale.",
      note: "Best for: Large-scale AI platforms and enterprise workloads"
    },
    performance: {
      latency: "Optimised for large-scale distributed queries",
      throughput: "Handles massive concurrent vector workloads"
    }
  }
};

const migrationDetails = {
  difficulty: "Medium",
  timeEstimate: "2–4 hours for 1M vectors",
  considerations: [
    "Matching index types and distance metrics",
    "GraphQL → client API translation",
    "Cluster sizing & resource planning",
    "Hybrid search configuration sync"
  ],
  howWeHelp: [
    "Automated index & metric alignment tools",
    "API translation helpers for hybrid modes",
    "Cluster sizing and best-practice recommendations",
    "Performance tuning after migration",
    "Data integrity and verification workflow"
  ]
};

const ComparisonSection = ({ title, icon: Icon, children }) => (
  <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-8">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
        <Icon className="w-5 h-5 text-black" />
      </div>
      <h2 className="text-2xl font-bold text-white">{title}</h2>
    </div>
    {children}
  </div>
);

const ProConList = ({ items, isPositive }) => (
  <ul className="space-y-3">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-3">
        <div
          className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
            isPositive ? "bg-white/20" : "bg-gray-700"
          }`}
        >
          {isPositive ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <X className="w-3 h-3 text-red-500" />
          )}
        </div>
        <span className="text-gray-300 text-sm">{item}</span>
      </li>
    ))}
  </ul>
);

const DatabaseCard = ({ data }) => (
  <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
    <h3 className="text-2xl font-bold text-white mb-2">{data.name}</h3>
    <p className="text-gray-400 text-sm mb-6">{data.tagline}</p>

    <div className="space-y-6">
      <div>
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Check className="text-green-500 w-4 h-4" />
          Advantages
        </h4>
        <ProConList items={data.pros} isPositive />
      </div>

      <div>
        <h4 className="text-gray-400 font-semibold mb-3 flex items-center gap-2">
          <X className="text-red-500 w-4 h-4" />
          Limitations
        </h4>
        <ProConList items={data.cons} />
      </div>
    </div>
  </div>
);

const PricingCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-white">{name}</h3>
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-800 text-gray-300 border border-gray-700">
        {data.tier}
      </span>
    </div>
    <p className="text-gray-300 text-sm mb-3">{data.description}</p>
    <p className="text-gray-500 text-xs italic">{data.note}</p>
  </div>
);

const PerformanceCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-white">{name}</h3>
    </div>
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Query Latency:</span>
        <span className="text-gray-300">{data.latency}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Throughput:</span>
        <span className="text-gray-300">{data.throughput}</span>
      </div>
    </div>
  </div>
);

export default function WeaviateVsMilvus() {
  return (
    <div className="bg-black min-h-screen py-10 px-6">
      <Navbar />
      <div className="max-w-6xl py-28 mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">
            Weaviate vs Milvus: Comparison 2026
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto">
            Flexible hybrid search vs massive-scale distributed vector engine. Compare deployment ease,
            performance tradeoffs, and operational considerations for modern AI systems.
          </p>
        </div>

        {/* Side-By-Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <DatabaseCard data={comparisonData.weaviate} />
          <DatabaseCard data={comparisonData.milvus} />
        </div>

        {/* Pricing */}
        <ComparisonSection title="Pricing Comparison" icon={DollarSign}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PricingCard data={comparisonData.weaviate.pricing} name="Weaviate" />
            <PricingCard data={comparisonData.milvus.pricing} name="Milvus" />
          </div>
        </ComparisonSection>

        {/* Performance */}
        <ComparisonSection title="Performance Metrics" icon={Gauge}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PerformanceCard data={comparisonData.weaviate.performance} name="Weaviate" />
            <PerformanceCard data={comparisonData.milvus.performance} name="Milvus" />
          </div>
        </ComparisonSection>

        {/* Migration */}
        <ComparisonSection title="Migration from Weaviate to Milvus" icon={RefreshCw}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Migration Complexity</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Difficulty Level:</span>
                  <span className="text-gray-300 font-semibold">{migrationDetails.difficulty}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Estimated Time:</span>
                  <span className="text-gray-300 text-sm">{migrationDetails.timeEstimate}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Key Considerations</h3>
              <ul className="space-y-2">
                {migrationDetails.considerations.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-gray-400 mt-0.5" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ComparisonSection>

        {/* Final Recommendation */}
        <div className="mt-16 bg-gray-900 border border-gray-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">
            Which Should You Choose?
          </h2>
          <div className="space-y-4 text-gray-300">
            <p>
              <strong className="text-white">Choose Weaviate if:</strong> You need flexible hybrid search,
              GraphQL support, and modular extensibility for mixed vector + metadata workloads.
            </p>
            <p>
              <strong className="text-white">Choose Milvus if:</strong> You’re scaling to very large
              datasets, require distributed performance, and can manage cluster infrastructure.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
